/**
 * CustomerInfo Component - 2-column customer information form
 */

import React from 'react';
import { CustomerInfo as CustomerInfoType, Distributor, Campaign, UIText } from './types';

interface CustomerInfoProps {
  customerInfo: CustomerInfoType;
  onChange: (field: keyof CustomerInfoType, value: string) => void;
  onCountryChange: (value: string) => void;
  dealerSelectionRestricted: boolean;
  isDistribuidorManual: boolean;
  uiText: UIText['customerInfo'];
  tradePartnerText: UIText['tradePartner'];
  distributorOptions: Distributor[];
  countryOptions: { code: string; label: string }[];
  campaignOptions?: Campaign[];
  campaignsEnabled?: boolean;
  validationErrors?: {
    distribuidor?: boolean;
    nombreFachberater?: boolean;
    nombre?: boolean;
    apellidos?: boolean;
    direccion?: boolean;
    ciudad?: boolean;
    nombreClinicaLaboratorio?: boolean;
    telefono?: boolean;
    email?: boolean;
    codigoPostal?: boolean;
    pais?: boolean;
  };
}

export const CustomerInfo: React.FC<CustomerInfoProps> = ({
  customerInfo,
  onChange,
  onCountryChange,
  dealerSelectionRestricted,
  isDistribuidorManual,
  uiText,
  tradePartnerText,
  distributorOptions,
  countryOptions,
  campaignOptions = [],
  campaignsEnabled = false,
  validationErrors = {}
}) => {
  const handleChange = (field: keyof CustomerInfoType) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    onChange(field, e.target.value);
  };

  const hasCountry = !!customerInfo.pais;
  // isDistribuidorManual (computed once in the parent, also sent with the
  // submitted order for HubSpot sync) is the single source of truth for
  // whether to show the free-text fallback.
  const showManualEntry = isDistribuidorManual;
  const dealerSelectDisabled = !dealerSelectionRestricted && !hasCountry;

  return (
    <>
      {/* Customer Information Section */}
      <section className="sprintry-customer-info">
        <div className="sprintry-customer-info__header">
          <h3 className="sprintry-customer-info__title">{uiText.title}</h3>
          <div className="sprintry-customer-info__request-type">
            <label htmlFor="requestType" className="sprintry-customer-info__request-type-label mb-0">
              {uiText.requestTypeLabel}:
            </label>
            <select
              id="requestType"
              value={customerInfo.requestType || 'quote'}
              onChange={handleChange('requestType')}
              className="sprintry-customer-info__request-type-select"
            >
              <option value="quote">{uiText.requestTypeQuote}</option>
              <option value="order">{uiText.requestTypeOrder}</option>
              <option value="lead">{uiText.requestTypeLead}</option>
            </select>
          </div>
          {campaignsEnabled && (
            <div className="sprintry-customer-info__campaign">
              <label htmlFor="hubspotCampaign" className="sprintry-customer-info__campaign-label mb-0">
                Campaign:
              </label>
              <select
                id="hubspotCampaign"
                value={customerInfo.hubspotCampaign || ''}
                onChange={(e) => {
                  const selectedCampaign = campaignOptions.find(c => c.id === e.target.value);
                  onChange('hubspotCampaign', e.target.value);
                  onChange('hubspotCampaignName', selectedCampaign?.name || '');
                }}
                className="sprintry-customer-info__campaign-select"
              >
                <option value="">No campaign selected</option>
                {campaignOptions.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="sprintry-customer-info__grid">
          {/* Column 1 */}
          <div className="sprintry-customer-info__column">
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.nombreClinicaLaboratorio ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="clinicName">{uiText.nombreClinica} <span className="required-mark">{uiText.required}</span></label>
              <input
                type="text"
                id="clinicName"
                value={customerInfo.nombreClinicaLaboratorio}
                onChange={handleChange('nombreClinicaLaboratorio')}
                required
                className="sprintry-form-field__input"
              />
            </div>
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.apellidos ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="lastName">{uiText.apellidos} <span className="required-mark">{uiText.required}</span></label>
              <input
                type="text"
                id="lastName"
                value={customerInfo.apellidos}
                onChange={handleChange('apellidos')}
                required
                className="sprintry-form-field__input"
              />
            </div>
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.codigoPostal ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="postalCode">{uiText.codigoPostal} <span className="required-mark">{uiText.required}</span></label>
              <input
                type="text"
                id="postalCode"
                value={customerInfo.codigoPostal}
                onChange={handleChange('codigoPostal')}
                required
                className="sprintry-form-field__input"
              />
            </div>
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.pais ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="country">{uiText.pais} <span className="required-mark">{uiText.required}</span></label>
              <select
                id="country"
                value={customerInfo.pais}
                onChange={(e) => onCountryChange(e.target.value)}
                required
                className="sprintry-form-field__input"
              >
                <option value="">{uiText.selectCountry || 'Select country'}</option>
                {countryOptions.map((country) => (
                  <option key={country.code} value={country.label}>{country.label}</option>
                ))}
              </select>
            </div>
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.email ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="customerEmail">{uiText.email} <span className="required-mark">{uiText.required}</span></label>
              <input
                type="email"
                id="customerEmail"
                value={customerInfo.email}
                onChange={handleChange('email')}
                required
                className="sprintry-form-field__input"
              />
            </div>
          </div>

          {/* Column 2 */}
          <div className="sprintry-customer-info__column">
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.nombre ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="firstName">{uiText.nombre} <span className="required-mark">{uiText.required}</span></label>
              <input
                type="text"
                id="firstName"
                value={customerInfo.nombre}
                onChange={handleChange('nombre')}
                required
                className="sprintry-form-field__input"
              />
            </div>
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.direccion ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="address">{uiText.direccion} <span className="required-mark">{uiText.required}</span></label>
              <input
                type="text"
                id="address"
                value={customerInfo.direccion}
                onChange={handleChange('direccion')}
                required
                className="sprintry-form-field__input"
              />
            </div>
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.ciudad ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="city">{uiText.ciudad} <span className="required-mark">{uiText.required}</span></label>
              <input
                type="text"
                id="city"
                value={customerInfo.ciudad}
                onChange={handleChange('ciudad')}
                required
                className="sprintry-form-field__input"
              />
            </div>
            <div className="sprintry-form-field">&nbsp;</div>
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.telefono ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="phone">{uiText.telefono} <span className="required-mark">{uiText.required}</span></label>
              <input
                type="tel"
                id="phone"
                value={customerInfo.telefono}
                onChange={handleChange('telefono')}
                required
                className="sprintry-form-field__input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trade Partner Section */}
      <section className="sprintry-trade-partner">
        <div className="sprintry-trade-partner__header">
          <h3 className="sprintry-trade-partner__title">{tradePartnerText.title}</h3>
        </div>
        <div className="sprintry-trade-partner__grid">
          {/* Column 1 */}
          <div className="sprintry-trade-partner__column">
            <div className={`sprintry-form-field sprintry-form-field--required ${validationErrors.distribuidor ? 'sprintry-form-field--error' : ''}`}>
              <label htmlFor="distributor">{tradePartnerText.distribuidor} <span className="required-mark">{uiText.required}</span></label>
              {showManualEntry ? (
                <input
                  type="text"
                  id="distributor"
                  value={customerInfo.distribuidor}
                  onChange={handleChange('distribuidor')}
                  placeholder={tradePartnerText.distribuidorManualPlaceholder || 'Enter trade partner name'}
                  required
                  className="sprintry-form-field__input"
                />
              ) : (
                <select
                  id="distributor"
                  value={customerInfo.distribuidor}
                  onChange={handleChange('distribuidor')}
                  required
                  disabled={dealerSelectDisabled}
                  className="sprintry-form-field__input"
                >
                  <option value="">
                    {dealerSelectDisabled
                      ? (tradePartnerText.selectCountryFirst || 'Select a country first')
                      : (tradePartnerText.selectDistributor || 'Select distributor')}
                  </option>
                  {distributorOptions.map((d) => (
                    <option key={d.id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              )}
              {showManualEntry && (
                <p className="sprintry-form-field__hint">{tradePartnerText.manualEntryHint}</p>
              )}
            </div>
          </div>

          {/* Column 2 */}
          <div className="sprintry-trade-partner__column">
            <div className="sprintry-form-field">
              <label htmlFor="fachberater">{tradePartnerText.nombreFachberater}</label>
              <input
                type="text"
                id="fachberater"
                value={customerInfo.nombreFachberater}
                onChange={handleChange('nombreFachberater')}
                className="sprintry-form-field__input"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
